import React, { useState, useEffect } from 'react';
import styles from '../components/styles/BookingPage.module.css';
import { db } from "../config/firebase";
import { collection, addDoc, query, where, getDocs } from "firebase/firestore";

function BookingPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time: ''
  });
  
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Generate available time slots
  const timeSlots = Array.from({ length: 17 }, (_, i) => {
    const hour = Math.floor(i / 2) + 9;
    const minute = i % 2 === 0 ? '00' : '30';
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  });

  // Check for blocked times when date changes
  useEffect(() => {
    if (formData.date) {
      fetchBlockedTimes();
    }
  }, [formData.date]);

  const fetchBlockedTimes = async () => {
    setIsLoading(true);
    try {
      const q = query(
        collection(db, "blockedTimes"),
        where("date", "==", formData.date)
      );
      const querySnapshot = await getDocs(q);
      const blockedTimeSlots = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setBlockedTimes(blockedTimeSlots);
      
      // If current selected time is now blocked, clear it
      if (formData.time) {
        const isTimeBlocked = isTimeSlotBlocked(formData.time, blockedTimeSlots);
        if (isTimeBlocked) {
          setFormData(prev => ({
            ...prev,
            time: ''
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching blocked times:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to check if a time slot is blocked
  const isTimeSlotBlocked = (timeSlot, blockedSlots = blockedTimes) => {
    // Check if time falls within any blocked range
    return blockedSlots.some(block => {
      // Better parsing of start and end times to ensure proper comparison
      const startParts = block.startTime.split(':');
      const endParts = block.endTime.split(':');
      
      const startHour = parseInt(startParts[0], 10);
      const startMinute = startParts.length > 1 ? parseInt(startParts[1], 10) : 0;
      
      const endHour = parseInt(endParts[0], 10);
      const endMinute = endParts.length > 1 ? parseInt(endParts[1], 10) : 0;
      
      const timeSlotParts = timeSlot.split(':');
      const slotHour = parseInt(timeSlotParts[0], 10);
      const slotMinute = timeSlotParts.length > 1 ? parseInt(timeSlotParts[1], 10) : 0;
      
      // Convert all to minutes for easy comparison
      const startTotalMinutes = startHour * 60 + startMinute;
      const endTotalMinutes = endHour * 60 + endMinute;
      const slotTotalMinutes = slotHour * 60 + slotMinute;
      
      return slotTotalMinutes >= startTotalMinutes && slotTotalMinutes <= endTotalMinutes;
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Double-check if time is blocked before submitting
      if (isTimeSlotBlocked(formData.time)) {
        setModalMessage('Sorry, this time slot is no longer available. Please select another time.');
        setShowModal(true);
        setIsLoading(false);
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }

      // Create booking
      const bookingData = {
        ...formData,
        status: 'pending',
        createdAt: new Date().toISOString(),
        // Flag to trigger the email confirmation Cloud Function
        sendConfirmation: true
      };

      // Add to Firestore - this will trigger the Cloud Function
      await addDoc(collection(db, "bookings"), bookingData);

      setModalMessage(`Booking request received! We've sent a confirmation email to ${formData.email} with your appointment details.`);
      setShowModal(true);

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        date: '',
        time: ''
      });

    } catch (error) {
      console.error("Booking error:", error);
      setModalMessage(error.message || 'Error creating booking. Please try again.');
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.container}>
        <h1>Book an Appointment</h1>
        
        <form onSubmit={handleSubmit} className={styles.bookingForm}>
          <div className={styles.formGroup}>
            <label>Name*</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Your full name"
            />
          </div>

          <div className={styles.formGroup}>
            <label>Email*</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="your.email@example.com"
            />
          </div>

          <div className={styles.formGroup}>
            <label>Phone*</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              placeholder="Your phone number"
            />
          </div>

          <div className={styles.formGroup}>
            <label>Service*</label>
            <select
              name="service"
              value={formData.service}
              onChange={handleInputChange}
              required
            >
              <option value="">Select a Service</option>
              <option value="Free Consultation">Free Consultation</option>
              <option value="Bridal Makeup">Bridal Makeup</option>
              <option value="Bridal Party Makeup">Bridal Party Makeup</option>
              <option value="Prom Makeup">Prom Makeup</option>
              <option value="Glam Makeup">Glam Makeup</option>
              <option value="Lashes">Lashes</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Date*</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              min={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label>Time*</label>
            {isLoading ? (
              <div className={styles.loading}>Loading available times...</div>
            ) : (
              <div className={styles.timeGrid}>
                {timeSlots.map(time => {
                  const isBlocked = isTimeSlotBlocked(time);
                  return (
                    <button
                      key={time}
                      type="button"
                      onClick={() => !isBlocked && handleInputChange({
                        target: { name: 'time', value: time }
                      })}
                      className={`${styles.timeButton} 
                        ${formData.time === time ? styles.selected : ''} 
                        ${isBlocked ? styles.blocked : ''}`}
                      disabled={isBlocked}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={isLoading || !formData.time || !formData.date || !formData.name || !formData.email || !formData.phone || !formData.service}
          >
            {isLoading ? "Processing..." : "Book Appointment"}
          </button>
        </form>

        {showModal && (
          <div className={styles.modal}>
            <div className={styles.modalContent}>
              <p>{modalMessage}</p>
              <button onClick={() => setShowModal(false)}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingPage;